import { msg } from "../../locales/translate.ts";
import type { BankAnalysisRequest } from "./overviewBankAnalysis";

export const emailSummaryCounts = ["attempts", "completed", "failed", "abandoned", "charged", "returned", "refunded", "cards", "mids_count", "clients_count", "currencies", "ips", "countries", "instruments", "distinct_names"] as const;
export const emailAmountFields = ["completed_amount", "failed_amount", "charged_amount", "refunded_amount"] as const;
export const emailIdentityFields = ["name", "email", "countryCode", "ip", "phone", "city", "state", "postalCode", "addressLine1", "addressLine2", "birthDate"] as const;
type Counts<K extends string> = Record<K, number | null>;
type Seen = { first_seen: string | null; last_seen: string | null };
export type EmailAmounts = { currency: string } & Counts<"attempts" | "completed" | typeof emailAmountFields[number]>;
export type EmailIdentity = { session_id: string | null; created_at: string | null } & Record<typeof emailIdentityFields[number], string | null>;
export type EmailMid = Seen & Counts<"attempts" | "completed" | "charged" | "returned"> & { id: string | null; name: string | null; amounts: EmailAmounts[]; identities: EmailIdentity[] };
export type EmailCard = Seen & Counts<"attempts" | "completed" | "charged" | "mids_count"> & { token: string | null; masked: string | null; scheme: string | null };
export interface EmailInfoData {
	email: string;
	period: { from: string; to: string; days: number | null; axis: string | null };
	currency: string | null;
	summary: Counts<typeof emailSummaryCounts[number]> & Seen;
	identity_names: string[];
	amounts: EmailAmounts[];
	clients: { id: string | null; name: string | null; mids: EmailMid[] }[];
	cards: EmailCard[];
}

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const object = (value: unknown) => { if (!record(value)) throw new Error(msg("invalidEmailInfoObject")); return value; };
const text = (value: unknown): string | null => typeof value === "string" && value.trim() ? value : null;
const id = (value: unknown) => typeof value === "number" && Number.isSafeInteger(value) ? String(value) : text(value);
const numeric = (value: unknown, integer = true): number | null => typeof value === "number" && Number.isFinite(value) && (!integer || (Number.isSafeInteger(value) && value >= 0)) ? value : null;
const counts = <K extends string>(value: Record<string, unknown>, keys: readonly K[]): Counts<K> => Object.fromEntries(keys.map(key => [key, numeric(value[key])])) as Counts<K>;
const seen = (value: Record<string, unknown>): Seen => ({ first_seen: text(value.first_seen), last_seen: text(value.last_seen) });
function list<T>(value: unknown, parse: (row: Record<string, unknown>) => T): T[] {
	if (value == null) return [];
	if (!Array.isArray(value)) throw new Error(msg("invalidEmailInfoList"));
	return value.map(row => parse(object(row)));
}
const amounts = (value: unknown): EmailAmounts[] => list(value, row => {
	if (typeof row.currency !== "string" || !/^[A-Z]{3}$/.test(row.currency)) throw new Error(msg("invalidCurrency"));
	return { currency: row.currency, ...counts(row, ["attempts", "completed"]),
		...Object.fromEntries(emailAmountFields.map(key => [key, numeric(row[key], false)])) as Counts<typeof emailAmountFields[number]> };
});

export function parseEmailInfo(raw: string, expected: BankAnalysisRequest): EmailInfoData | null {
	try {
		const response = object(JSON.parse(raw));
		if (response.error) return null;
		let data = object(response.data);
		// The HTTP adapter may retain the server's data envelope.
		if (record(data.data)) { if (data.error) return null; data = data.data; }
		if (data.error || expected.bank.type !== "email" || data.email !== expected.bank.value) return null;
		const period = object(data.period), summary = object(data.summary);
		if (period.from !== expected.from || period.to !== expected.to || (expected.currency && data.currency !== expected.currency)) return null;
		if (numeric(summary.attempts) === null) return null;
		return {
			email: data.email as string, currency: text(data.currency),
			period: { from: expected.from, to: expected.to, days: numeric(period.days), axis: text(period.axis) },
			summary: { ...counts(summary, emailSummaryCounts), ...seen(summary) },
			identity_names: Array.isArray(data.identity_names) ? data.identity_names.filter((name): name is string => typeof name === "string" && !!name.trim()) : [],
			amounts: amounts(data.amounts),
			clients: list(data.clients, client => ({ id: id(client.id), name: text(client.name), mids: list(client.mids, mid => ({
				id: id(mid.id), name: text(mid.name), ...counts(mid, ["attempts", "completed", "charged", "returned"]), ...seen(mid), amounts: amounts(mid.amounts),
				identities: list(mid.identities, identity => ({ session_id: id(identity.session_id), created_at: text(identity.created_at),
					...Object.fromEntries(emailIdentityFields.map(key => [key, text(identity[key])])) as Record<typeof emailIdentityFields[number], string | null> })),
			})) })),
			cards: list(data.cards, card => ({ token: text(card.token), masked: text(card.masked), scheme: text(card.scheme), ...seen(card), ...counts(card, ["attempts", "completed", "charged", "mids_count"]) })),
		};
	} catch { return null; }
}

export function emailInfoDate(value: string | null, language: string): string {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" }).format(date);
}

/** A part of a total; unknown or empty totals give no share instead of a fabricated 0%. */
export const emailShare = (part: number | null, total: number | null): number | null => part === null || total === null || total <= 0 ? null : part / total;

/** UTC calendar days from the first to the last payment, both included. */
export function emailActiveDays({ first_seen, last_seen }: Seen): number | null {
	const first = first_seen ? Date.parse(first_seen) : NaN, last = last_seen ? Date.parse(last_seen) : NaN;
	if (Number.isNaN(first) || Number.isNaN(last) || last < first) return null;
	const day = (time: number) => Math.floor(time / 86_400_000);
	return day(last) - day(first) + 1;
}

/** Summary banner heuristics: they point at facts worth a look and are not rule results. */
export const emailSignalLimits = { cards: 3, clients_count: 3, distinct_names: 2, countries: 2 } as const;
export const emailLowConversion = { attempts: 5, rate: .5 } as const;
export type EmailSignal = { key: "charged" | "returned" | "refunded" | "conversion" | keyof typeof emailSignalLimits; tone: "danger" | "warn"; value: number };
export type EmailVerdict = "danger" | "warn" | "ok" | "empty";

export function emailSignals(summary: EmailInfoData["summary"]): EmailSignal[] {
	const signals: EmailSignal[] = [];
	if (summary.charged) signals.push({ key: "charged", tone: "danger", value: summary.charged });
	if (summary.returned) signals.push({ key: "returned", tone: "warn", value: summary.returned });
	if (summary.refunded) signals.push({ key: "refunded", tone: "warn", value: summary.refunded });
	const rate = emailShare(summary.completed, summary.attempts);
	if (rate !== null && summary.attempts! >= emailLowConversion.attempts && rate < emailLowConversion.rate) signals.push({ key: "conversion", tone: "warn", value: rate });
	for (const key of Object.keys(emailSignalLimits) as (keyof typeof emailSignalLimits)[]) {
		const value = summary[key];
		if (value !== null && value >= emailSignalLimits[key]) signals.push({ key, tone: "warn", value });
	}
	return signals;
}

export const emailVerdict = (summary: EmailInfoData["summary"], signals: EmailSignal[]): EmailVerdict =>
	!summary.attempts ? "empty" : signals.some(signal => signal.tone === "danger") ? "danger" : signals.length ? "warn" : "ok";
